/* <quantity-breaks> — native Mengenrabatt tiers (live parity: BOGOS volume widget).
   Prices are DISPLAY-ONLY: line = unit × qty × (1 − pct/100); the struck price is the
   undiscounted unit × qty (matches the live widget). The checkout discount itself comes
   from an automatic quantity discount / the BOGOS app — this component only adds
   qty × current variant in one /cart/add.js call and opens the drawer.
   Follows the price on kk:variant:changed (same contract as sticky ATC / price block). */
class QuantityBreaks extends HTMLElement {
  connectedCallback() {
    this.unit = Number(this.dataset.unitPrice) || 0;
    this.variantId = this.dataset.variantId;
    this.rows = Array.from(this.querySelectorAll('[data-qb-row]'));
    if (!this.rows.length) return;
    this.totalEl = this.querySelector('[data-qb-total]');
    this.totalCompareEl = this.querySelector('[data-qb-total-compare]');
    this.atc = this.querySelector('[data-qb-atc]');
    this.atcLabel = this.querySelector('[data-qb-atc-label]');
    this.errEl = this.querySelector('[data-qb-error]');

    if (!this.querySelector('input:checked')) {
      const first = this.rows[this.rows.length - 1].querySelector('input');
      if (first) first.checked = true;
    }
    this.rows.forEach((row) => {
      const radio = row.querySelector('input');
      if (radio) radio.addEventListener('change', () => this.update());
    });
    if (this.atc) this.atc.addEventListener('click', () => this.add());

    if (window.KK && window.KK.pubsub) {
      this._unsub = window.KK.pubsub.subscribe('kk:variant:changed', (p) => {
        if (!p || p.sectionId !== this.dataset.sectionId || !p.variant) return;
        this.variantId = String(p.variant.id);
        this.unit = Number(p.variant.price) || this.unit;
        this.update();
      });
    }
    this.update();
  }

  disconnectedCallback() { if (this._unsub) this._unsub(); }

  money(cents) {
    if (window.KK && window.KK.money) return window.KK.money(cents);
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  selectedRow() {
    return this.rows.find((r) => { const i = r.querySelector('input'); return i && i.checked; }) || this.rows[0];
  }

  update() {
    this.rows.forEach((row) => {
      const qty = Number(row.dataset.qty) || 1;
      const pct = Number(row.dataset.percent) || 0;
      const line = Math.round(this.unit * qty * (100 - pct) / 100);
      const full = this.unit * qty;
      const priceEl = row.querySelector('[data-qb-price]');
      const compareEl = row.querySelector('[data-qb-compare]');
      if (priceEl) priceEl.textContent = this.money(line);
      if (compareEl) { compareEl.textContent = this.money(full); compareEl.hidden = full <= line; }
      const input = row.querySelector('input');
      row.classList.toggle('is-selected', !!(input && input.checked));
    });
    const sel = this.selectedRow();
    const qty = Number(sel.dataset.qty) || 1;
    const pct = Number(sel.dataset.percent) || 0;
    const line = Math.round(this.unit * qty * (100 - pct) / 100);
    const full = this.unit * qty;
    if (this.totalEl) this.totalEl.textContent = this.money(line);
    if (this.totalCompareEl) { this.totalCompareEl.textContent = this.money(full); this.totalCompareEl.hidden = full <= line; }
    if (this.atcLabel) {
      this.atcLabel.textContent = pct > 0
        ? (this.dataset.ctaSets || 'SETS ZUM WARENKORB HINZUFÜGEN') + ' (' + pct + '% RABATT)'
        : (this.dataset.ctaSingle || 'ZUM WARENKORB HINZUFÜGEN');
    }
  }

  /* Guard symmetry (buy-path audit 2026-07-31): _setBusy sets BOTH `disabled` and
     aria-busy and add() checks BOTH, so a rapid double tap cannot buy two sets.
     The pre-flight disabled state is remembered so a sold-out variant stays disabled. */
  _setBusy(on) {
    if (!this.atc) return;
    if (on) {
      this._prevDisabled = this.atc.disabled;
      this.atc.disabled = true;
      this.atc.setAttribute('aria-busy', 'true');
    } else {
      this.atc.disabled = !!this._prevDisabled;
      this.atc.removeAttribute('aria-busy');
    }
  }

  /* Contract rule 2: render err.userMessage (localized German) — never err.message
     or any raw exception text. Fall back to the locale string this block renders. */
  _userMessage(err) {
    if (err && typeof err.userMessage === 'string' && err.userMessage) return err.userMessage;
    const fromDom = this.errEl && this.errEl.getAttribute('data-error-text');
    if (fromDom) return fromDom;
    const i18n = window.KK && window.KK.i18n;
    if (i18n && i18n.cartAddError) return i18n.cartAddError;
    return 'Der Artikel konnte nicht hinzugefügt werden. Bitte versuche es erneut.';
  }

  showError(err) {
    if (!this.errEl) return;
    this.errEl.textContent = this._userMessage(err);
    this.errEl.hidden = false;
  }

  add() {
    if (!window.KK || !window.KK.cart || !this.variantId || !this.atc) return;
    if (this._pending) return;
    if (this.atc.disabled || this.atc.getAttribute('aria-busy') === 'true') return;
    const sel = this.selectedRow();
    const qty = Number(sel.dataset.qty) || 1;
    if (this.errEl) this.errEl.hidden = true;
    this._pending = true;
    this._setBusy(true);

    // The prelude runs after the busy state is set — a synchronous throw here would
    // otherwise latch the button dead forever. Contract rule 4: restore in finally.
    try {
      // buy-path audit 2026-07-31: getElementById('cart-drawer') never matched — the
      // drawer element's DOM id is `CartDrawer`. Read the section id it stamps instead,
      // otherwise the drawer opens showing the cart from BEFORE this add.
      const ids = [];
      const drawer = document.querySelector('cart-drawer[data-section-id]');
      if (drawer) ids.push(drawer.getAttribute('data-section-id'));
      ids.push('header');
      window.KK.cart.add({ id: this.variantId, quantity: qty }, { sections: ids.join(',') })
        .then(() => {
          if (window.KK.pubsub) window.KK.pubsub.publish('kk:drawer:open', { id: 'cart' });
        })
        .catch((err) => { this.showError(err); })
        .finally(() => {
          this._pending = false;
          this._setBusy(false);
        });
    } catch (err) {
      this._pending = false;
      this._setBusy(false);
      console.warn('[kk] quantity-break add could not be started', err);
      this.showError(err);
    }
  }
}
if (!customElements.get('quantity-breaks')) customElements.define('quantity-breaks', QuantityBreaks);
